// src/oura/screens/BusinessDetail.tsx
//
// Staff drill-in chrome (gameplan A3). With the business sidebar live it is
// the shared Oura sidebar plus the page header, on the page ground; without
// it, the legacy breadcrumb, header and tab strip. Children are the existing
// screens, embedded; the History tab (gameplan A4) is rendered here directly
// from the score history endpoint.

import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
    ChevronRightIcon,
    LayoutDashboardIcon,
    SparklesIcon, EyeIcon,
    FilePenLineIcon,
    SearchCheckIcon,
    GlobeIcon,
    ClipboardListIcon,
    HistoryIcon,
    FileTextIcon,
    ShareIcon,
    RefreshCwIcon,
    PlugIcon,
    FolderOpenIcon,
    Building2Icon,
    ActivityIcon,
    FlagIcon,
    InfoIcon,
    MessageSquareIcon,
    ScrollTextIcon,
    CalendarClockIcon,
    PackageIcon,
    Loader2Icon,
    PencilIcon,
    ListChecksIcon,
    UserPlusIcon,
    type LucideIcon,
} from "lucide-react";
import type { Business, ScoreSnapshot } from "../contract";
import type { View } from "../nav";
import { openClientJump } from "../components/ClientJump";
import { OURA_ROOT, OURA_BUSINESS_GROUPS, ouraSectionFor, isBusinessSidebarLive, showOnboarding } from "../nav";
import { DELIVERY_ON } from "../delivery/flag";
import type { StartResult } from "../aeo";
import { Card, CardBody, CardHeader } from "../primitives/Card";
import { EmptyState } from "../primitives/EmptyState";
import { Button } from "../primitives/Button";
import { Chip } from "../primitives/Chip";

import { Tooltip } from "@component/tooltip";
import { cn } from "../lib/cn";
import { summariseSpend, summariseTotal } from "../lib/spend";
import { useSnackbar } from "@hook/use-snackbar";
import { NewScanModal } from "../components/NewScanModal";
import {
    getAssessment,
    getScoreHistory,
    type AeoScoreHistoryEntry,
    type AeoSubmittedInputs,
} from "@service/aeo";
import { isClientReviewLive } from "@service/aeo/clientReview";
import { renameBusiness } from "@service/aeo";
import { isOuraProd, useOuraRole } from "../role";
import { joinDotted, shortDateOrNull } from "../lib/oura";
import { questionBreakBetween } from "../trendBreak";
import { FIRST_CHECK_MINUTES, callName } from "../onboarding/derive";
import { ArrowLink, OuraSidebar, PAGE_GROUND, PageSlots, PillButton, Seg, SURFACE, Tag, TYPE, type OuraSidebarGroup } from "@oura/ui";
import { SCAN_DRAFT_UI_ON, scanDraftMode } from "../newscan/flags";
import { useScanningSites } from "../newscan/useScanningSites";
import { chooseSignatureQuestion } from "@service/aeo/onboarding";
import { findOpenScanDraft, recipeUpdateFor } from "@service/aeo/scanDraft";
import { isFirstCheckDraft } from "@service/aeo/scanDraftTypes";
import { RescanSheet } from "../components/RescanSheet";
import { BusinessSpendCard } from "../components/BusinessSpendCard";
import { NewClientDialog } from "../components/onboarding/NewClientDialog";
import { announceDeliveryChange } from "@service/aeo/delivery";
import { type HubAct, type HubFacts, type HubPill, firstResultsAwaitSend, isEditRescanPill } from "../overview/hubStage";
import { HubContext, useHubStage, type HubContextValue } from "../overview/useHubStage";
import { useDraftNoticeSnackbar, useHubActions } from "../overview/useHubActions";
import { ASK_OURA_ON, AskOuraBar, OuraFace, ASK_KEY_HINT, toggleAsk, useAskOpen } from "@oura/ask";

// The approval layer runs on an in-memory mock until its endpoints ship
// (ourteam-backend#4735). Staging shows it; production hides it, so no real
// user is ever shown mock data.
const showClientApprovals = isClientReviewLive || !isOuraProd();

export type DetailTab = "dashboard" | "questions" | "setup" | "pages" | "documents" | "knowledge" | "connectors" | "todo" | "delivery" | "reviews" | "contract" | "timeline" | "activity" | "history" | "onboarding";

// Ordered as the work is actually done: land on the roll-up, look at the pages
// themselves, then the two diagnoses of them (how the site performs, how AI
// sees it), then the plan that comes out of it, and finally the plumbing that
// feeds the whole thing. Connectors sits last on purpose — it is setup, touched
// once, and it used to sit between two surfaces people move between daily.
const TABS: Array<{ view: DetailTab; label: string; icon: typeof LayoutDashboardIcon }> = [
    { view: "dashboard", label: "Overview", icon: LayoutDashboardIcon },
    // The page-centric WORK surface (selection + the copy/link workshop), where
    // Site Audit stays issue-centric.
    { view: "pages", label: "Pages", icon: FileTextIcon },
    { view: "setup", label: "Site Audit", icon: GlobeIcon },
    { view: "questions", label: "AI Visibility", icon: EyeIcon },
    { view: "todo", label: "Tracker", icon: ClipboardListIcon },
    // Right after the plan: the client's documents, their requests, the
    // questions already sent and what came back. Named "Client", as the rail
    // names it: the approval table is gone (YW, 29 Sep 2026).
    { view: "reviews", label: "Client", icon: MessageSquareIcon },
    // What was sold, then when it lands. In that order because one is the
    // source of the other: the schedule is computed FROM the contract, so
    // reading them the other way round invites arguing with a date whose reason
    // you have not seen yet.
    { view: "contract", label: "Contract", icon: ScrollTextIcon },
    { view: "timeline", label: "Timeline", icon: CalendarClockIcon },
    // Between the approvals and the plumbing, because that is where it is
    // used: what the client SENT sits next to what the client was ASKED, and
    // before the machinery that reads it.
    // Documents holds Client documents and Knowledge as page tabs (DocumentsTabs).
    { view: "documents", label: "Documents", icon: FolderOpenIcon },
    { view: "connectors", label: "Connectors", icon: PlugIcon },
    // Last, after the plumbing. It is the record of what has already happened,
    // read when somebody asks what we did rather than while doing it — and it
    // only became worth a tab once #4531 made the platform log itself, instead
    // of the tab showing whatever one person remembered to type.
    { view: "activity", label: "Activity", icon: ActivityIcon },
    // Restored in #3521. It was removed in #3212 because `buildLiveSnapshot()`
    // had no history source, so the tab could only ever render an empty list
    // under a "Mock data" footer. ourteam-backend#4572 is that source.
    { view: "history", label: "History", icon: HistoryIcon },
    // From a signed quote to an active client. Hidden in production until its
    // endpoints ship (see `showOnboarding`).
    ...(showOnboarding ? [{ view: "onboarding" as DetailTab, label: "Onboarding", icon: FlagIcon }] : []),
];

// With Delivery on, one Delivery tab stands where Tracker was, and Contract and
// Timeline live inside it (its Plan and By month views), as on the rail.
const SHOWN_TABS = DELIVERY_ON
    ? TABS.filter((t) => t.view !== "contract" && t.view !== "timeline").map((t) => (t.view === "todo" ? { view: "delivery" as const, label: "Delivery", icon: PackageIcon } : t))
    : TABS;

/**
 * Renaming a business, and setting its city (#3975).
 *
 * The label only, and the dialog says so: the domain underneath is what every
 * other table is keyed on. Trimmed and length-capped here as well as on the
 * server, because the server's 400 costs a round trip to say what the field
 * could have said immediately.
 *
 * The city is here because it is the same PATCH and the same name row on
 * screen — and because a business with no city gets no `<Location>` part in
 * its doc names, which until now could only be fixed by re-running a paid
 * scan (ourteam-backend#5257). It carries no underscore: that would split a
 * doc name. A city is sent only when it changed, so a plain rename behaves
 * exactly as it did before on a server that does not take one yet.
 */
function RenameDialog({
    business,
    onClose,
    onSaved,
    onRouteAbsent,
}: {
    business: Business;
    onClose: () => void;
    onSaved: () => void | Promise<void>;
    onRouteAbsent: () => void;
}) {
    const [name, setName] = useState(business.name);
    const [city, setCity] = useState(business.city ?? "");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const trimmed = name.trim();
    const cityTrimmed = city.trim();
    const savedCity = (business.city ?? "").trim();
    const cityChanged = cityTrimmed !== savedCity;
    // The server refuses it for the same reason: a doc name's segments are
    // underscore-separated, so one in the city would split the name.
    const cityBad = cityTrimmed.includes("_");
    const unchanged = trimmed === business.name.trim() && !cityChanged;

    const save = async () => {
        if (!trimmed || saving) return;
        if (cityBad) {
            setError("A city cannot contain an underscore — it would split the name of every document Oura files for this client.");
            return;
        }
        setSaving(true);
        setError(null);
        const r = await renameBusiness(business.id, trimmed, cityChanged ? cityTrimmed : undefined);
        setSaving(false);
        if (r.ok) {
            // A server that does not take a city yet drops it in silence. Say
            // so rather than close on a save that half happened.
            if (cityChanged && r.cityKept !== undefined && (r.cityKept ?? "").trim() !== cityTrimmed) {
                await onSaved();
                setError("The name was saved. The city was not: this environment's API does not take one yet.");
                return;
            }
            await onSaved();
            onClose();
            return;
        }
        if (r.kind === "absent") {
            // Not deployed here. SAY so and leave the dialog open — closing it
            // and quietly removing the pencil looks like the click did nothing,
            // and an operator repeats a rename they think they mis-clicked. The
            // pencil goes when they close it.
            setError("Renaming is not available on this environment yet — the API route is not deployed. "
                + "Nothing was changed.");
            onRouteAbsent();
            return;
        }
        setError(r.message);
    };

    return (
        <div className="fixed inset-0 z-50">
            <div className="absolute inset-0 bg-black/30" onClick={onClose} />
            <div className="absolute left-1/2 top-1/2 w-full max-w-[460px] -translate-x-1/2 -translate-y-1/2 rounded-xl border border-gray-200 bg-white p-6 shadow-xl">
                <h2 className="my-0 text-lg font-semibold leading-snug text-gray-900">Name and city</h2>
                <p className="my-0 mt-1 text-sm text-gray-500">
                    The name on every screen and report, and the city its documents are named with. {business.domain} does
                    not change — it is what the scans, connectors and documents are filed under.
                </p>
                <label className="mt-4 block">
                    <span className="mb-1 block text-xs font-medium text-gray-700">Business name</span>
                    <input
                        autoFocus
                        value={name}
                        maxLength={255}
                        onChange={(e) => setName(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") void save(); }}
                        className="h-9 w-full rounded-lg border border-gray-300 px-3 text-sm text-gray-900 outline-none focus:border-blue-500"
                    />
                </label>
                <label className="mt-3 block">
                    <span className="mb-1 block text-xs font-medium text-gray-700">City</span>
                    <input
                        value={city}
                        maxLength={255}
                        placeholder="Kuala Lumpur"
                        aria-invalid={cityBad || undefined}
                        onChange={(e) => { setCity(e.target.value); if (error) setError(null); }}
                        onKeyDown={(e) => { if (e.key === "Enter") void save(); }}
                        className="h-9 w-full rounded-lg border border-gray-300 px-3 text-sm text-gray-900 outline-none focus:border-blue-500"
                    />
                    <span className="mt-1 block text-xs leading-relaxed text-gray-500">
                        Where the business is. Documents with no outlet are named with it, and a city set here is not overwritten by the next scan. Empty clears it.
                    </span>
                </label>
                {error && <p className="my-0 mt-2 text-xs leading-relaxed text-rose-700">{error}</p>}
                <div className="mt-5 flex justify-end gap-2">
                    <Button variant="outline" size="sm" disabled={saving} onClick={onClose}>Cancel</Button>
                    <Button size="sm" disabled={!trimmed || unchanged || saving} onClick={() => void save()}>
                        {saving ? <><Loader2Icon className="h-3.5 w-3.5 animate-spin" />Saving…</> : "Save"}
                    </Button>
                </div>
            </div>
        </div>
    );
}

/** The leading icon of each stage pill in the header. */
const HUB_PILL_ICON: Record<HubAct["kind"], LucideIcon> = {
    draft_new: ListChecksIcon,
    first_check: ListChecksIcon,
    continue: FilePenLineIcon,
    client_signed: UserPlusIcon,
    rescan: RefreshCwIcon,
    edit: ListChecksIcon,
    onboarding: FlagIcon,
    signature: FlagIcon,
    contract: ScrollTextIcon,
    timeline: CalendarClockIcon,
};

/** Remembered per operator, not per business: collapsing is about screen width. */
const SIDEBAR_COLLAPSED_KEY = "oura.businessSidebar.collapsed";

/**
 * Views with no place on the rail, which still need a page title. The section
 * they light (through `alsoActive`) is where they are reached from, not their
 * name.
 */
const OFF_RAIL_TITLE: Partial<Record<View, string>> = { search: "Search performance" };

/**
 * The screens this redesign did not touch and that run wide: the Page
 * workshop's table, the timeline, the contract, the knowledge list. They keep
 * the width they had before the chrome capped content (the merchant screens'
 * 1500px); the redesigned screens are laid out for 6xl.
 */
const WIDE_VIEWS: ReadonlySet<View> = new Set<View>(["pages", "timeline", "contract", "documents", "knowledge"]);

/**
 * The per-business chrome: the shared Oura sidebar, then the page header and
 * the screen on the page ground.
 *
 * The sidebar names the business, so the header is free to name the SCREEN:
 * the over-line carries the business facts and the last scan, the title is
 * the section, and the screen supplies the one-sentence verdict under it and
 * any actions of its own through <PageSentence> and <PageActions>.
 */
/**
 * Overview, Visibility and Site: the screens about the measurement. The paid
 * "Run AI check again" and Share report sit on these only, so the work screens
 * (Delivery, Client, Documents...) keep their header for their own press and
 * nobody starts a paid run from a to-do list by mistake (Jack, 22 Oct 2026).
 */
const MEASURE_SECTION_IDS: ReadonlySet<string> = new Set(OURA_BUSINESS_GROUPS[0].sections.map((s) => s.id));
function isMeasureView(view: View): boolean {
    const id = ouraSectionFor(view)?.id;
    return id !== undefined && MEASURE_SECTION_IDS.has(id);
}

function SidebarChrome({
    business,
    active,
    lastScanLabel,
    onNavigate,
    onOpenReport,
    onRescan,
    onRename,
    canRename,
    readOnly,
    scanPicker,
    hubPills,
    onHubAct,
    scanningTag,
    shareSecondary,
    shareLocked,
    children,
}: {
    business: Business;
    active: DetailTab;
    lastScanLabel: string;
    onNavigate: (v: View) => void;
    onOpenReport: () => void;
    onRescan: () => void;
    /** A past scan is on screen: nothing in this chrome may write (#3933). */
    readOnly?: boolean;
    /** The scan version picker, when this business has more than one scan. */
    scanPicker?: ReactNode;
    /**
     * New scan switch on, staff: the stage's pills (at most two) in place of
     * Rerun assessment (JOURNEY-SPEC v1.1 §2.2). Undefined: today's header.
     */
    hubPills?: HubPill[];
    onHubAct?: (a: HubAct) => void;
    /** "Scanning · about 40 minutes" while a scan runs for this business (New scan switch on). */
    scanningTag?: ReactNode;
    /** Another press on screen is the blue one (or the stage is still being read): Share report stays white. */
    shareSecondary?: boolean;
    /**
     * A One Setup client whose first results are not sent yet: Share report is shut,
     * with this line as its reason. Nothing reaches the client before Send.
     */
    shareLocked?: string | null;
    /** Opens the rename dialog. Absent or `canRename` false: no pencil. */
    onRename?: () => void;
    canRename?: boolean;
    children?: ReactNode;
}) {
    const view = active as View;
    const section = ouraSectionFor(view);
    const measuring = isMeasureView(view);
    const role = useOuraRole();
    const askOpen = useAskOpen();
    /** False for an off-rail view the section only lights (Search performance). */
    const isMember = section?.views.some((v) => v.view === view) ?? false;

    const groups: OuraSidebarGroup[] = OURA_BUSINESS_GROUPS.map((group) => ({
        key: group.label,
        label: group.label,
        items: group.sections
            // Same gate the tab strip uses: the approval layer runs on mock
            // endpoints in production until ourteam-backend#4735 ships, so no
            // real user is shown mock data.
            .filter((s) => s.id !== "client" || showClientApprovals)
            .filter((s) => s.id !== "onboarding" || showOnboarding)
            .map((s) => ({
                key: s.id,
                label: s.label,
                icon: s.icon,
                active: s.id === section?.id,
                onClick: () => onNavigate(s.views[0].view),
            })),
    }));

    return (
        <div className="flex min-h-screen items-stretch">
            <OuraSidebar
                ariaLabel={`${business.name} sections`}
                storageKey={SIDEBAR_COLLAPSED_KEY}
                // Up, not back (house §12). The platform sidebar steps aside
                // in a business tab, so this is the only route to the list.
                backlink={{ to: `${OURA_ROOT}/businesses`, label: "Businesses", title: "All businesses" }}
                identity={{
                    name: business.name,
                    sub: business.domain,
                    // Another client, same screen, without going back to the list (Jack, 22 Oct 2026).
                    switcher: { onClick: openClientJump, title: "Switch client (Ctrl K)" },
                    // The icon the scan found (ourteam-backend#5901); before a
                    // rescan has looked, a guess at the domain's /favicon.ico.
                    icon: business.faviconUrl ?? (business.domain ? `https://${business.domain.replace(/^https?:\/\//, "").replace(/\/.*$/, "")}/favicon.ico` : null),
                    // The NAME only. The domain under it is the key every
                    // other table stores (connectors, drafts, knowledge,
                    // trackers), so renaming that would be a migration.
                    action: canRename && onRename ? (
                        <button
                            type="button"
                            onClick={onRename}
                            title="Edit the name and city"
                            aria-label="Edit this business's name and city"
                            className="shrink-0 rounded p-0.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
                        >
                            <PencilIcon className="h-3.5 w-3.5" />
                        </button>
                    ) : undefined,
                }}
                groups={groups}
                // Ask Oura (staff only, behind VITE_ENABLE_OURA_ASK): first row under the business name.
                lead={
                    ASK_OURA_ON && role === "oura-staff"
                        ? {
                              key: "ask-oura",
                              label: "Ask Oura",
                              iconNode: <OuraFace size={17} />,
                              kbd: ASK_KEY_HINT,
                              active: askOpen,
                              ownWash: true,
                              onClick: toggleAsk,
                          }
                        : undefined
                }
            />

            {/* `min-w-0` is what stops a wide table inside a screen from
                widening the flex row and pushing the sidebar off. */}
            <div className={cn("min-w-0 flex-1 px-4 py-7 md:px-8", PAGE_GROUND)}>
                <div className={cn("mx-auto", WIDE_VIEWS.has(view) ? "max-w-[1500px]" : "max-w-6xl")}>
                    {/* Ask Oura (staff only, behind VITE_ENABLE_OURA_ASK): above the page title. */}
                    {/* Staff only, same predicate as the sidebar lead above: a merchant never sees the bar. */}
                    {ASK_OURA_ON && role === "oura-staff" && <AskOuraBar className="mb-6" />}
                    <PageSlots
                        head={{
                            over: joinDotted(business.domain, business.vertical, business.city, lastScanLabel),
                            title: OFF_RAIL_TITLE[view] ?? section?.label ?? business.name,
                            status: scanningTag ?? undefined,
                            actions: (
                                <>
                                    {scanPicker}
                                    {hubPills ? (
                                        // A past scan on screen: the version picker only.
                                        !readOnly && hubPills.filter((p) => measuring || p.act.kind !== "rescan").map((p) => (
                                            <PillButton
                                                key={p.label}
                                                variant={p.primary ? "primary" : "secondary"}
                                                icon={HUB_PILL_ICON[p.act.kind]}
                                                onClick={() => onHubAct?.(p.act)}
                                            >
                                                {p.label}
                                            </PillButton>
                                        ))
                                    ) : measuring && (
                                        <PillButton icon={RefreshCwIcon} onClick={onRescan} disabled={readOnly}>
                                            Run AI check again
                                        </PillButton>
                                    )}
                                    {/* One blue pill per screen: a stage press that is ours outranks sharing. */}
                                    {!measuring ? null : shareLocked ? (
                                        <Tooltip content={shareLocked}>
                                            <span tabIndex={0} title={shareLocked} aria-label={`Share report. ${shareLocked}`} className="inline-flex rounded-full">
                                                <PillButton variant="secondary" icon={ShareIcon} disabled aria-hidden tabIndex={-1}>
                                                    Share report
                                                </PillButton>
                                            </span>
                                        </Tooltip>
                                    ) : (
                                        <PillButton
                                            variant={shareSecondary ? "secondary" : "primary"}
                                            icon={ShareIcon}
                                            onClick={onOpenReport}
                                        >
                                            Share report
                                        </PillButton>
                                    )}
                                </>
                            ),
                        }}
                    >
                        {/* A section with more than one member view: Site
                            (Site, Page workshop), Plan (Contract, Timeline),
                            History (Scans, Activity). Each member keeps its own
                            route, so this is chrome over the router, not a
                            replacement for it; the Page workshop in particular
                            cannot fold into a tab of the Site screen. */}
                        {section && isMember && section.views.length > 1 && (
                            <div className="mb-5">
                                <Seg
                                    ariaLabel={`${section.label} views`}
                                    items={section.views.map((v) => ({ key: v.view, label: v.label }))}
                                    value={view}
                                    onChange={onNavigate}
                                />
                            </div>
                        )}
                        {children}
                    </PageSlots>
                </div>
            </div>
        </div>
    );
}

/**
 * The score series for one business (#3521, BE ourteam-backend#4572).
 *
 * Rewritten rather than adapted from the old monthly-rollup panel: this endpoint
 * reports AEO and SEO separately per assessment, and folding it into one "total"
 * would throw away exactly the comparison the feature was asked for.
 *
 * Two things it must not get wrong, both stated in the contract:
 *   • A GATED run is shown, with no score. Dropping it would leave an
 *     unexplained gap in the timeline; rendering it as 0 would be a lie.
 *   • 0 is a real score, so it is rendered as 0 — never as "—".
 */
function ScoreHistoryPanel({ businessId }: { businessId?: string }) {
    // What this client has cost us sits above its scans, for staff only (#4816): spend is ours, not the client's.
    const isStaff = useOuraRole() === "oura-staff";
    return (
        <div className="space-y-4">
            {/* Keyed by the business, so switching clients drops the last one's figures at once (Jack, #4819). */}
            {isStaff && <BusinessSpendCard key={businessId ?? "no-business"} businessId={businessId} />}
            <ScoreHistoryList businessId={businessId} />
        </div>
    );
}

function ScoreHistoryList({ businessId }: { businessId?: string }) {
    const [entries, setEntries] = useState<AeoScoreHistoryEntry[] | null>(null);

    useEffect(() => {
        if (!businessId) { setEntries([]); return; }
        let cancelled = false;
        void getScoreHistory(businessId).then((rows) => {
            if (!cancelled) setEntries(rows);
        });
        return () => { cancelled = true; };
    }, [businessId]);

    const rows = entries ?? [];
    // Only real scores move a delta. A gated run has none, so the comparison
    // skips it rather than inventing a drop to zero and back.
    const scored = rows.filter((r) => r.aeo_score !== null || r.seo_score !== null);
    const spendTotal = summariseTotal(rows);

    // "28 Sep 2026", as every other Oura date: the en-GB locale prints "Sept".
    const fmtDate = (iso: string) => shortDateOrNull(iso) ?? iso;
    /**
     * Rows grouped under the day they ran, with a time on each (#4025 FE-19).
     *
     * Several scans on one day were five rows reading the same date and nothing
     * else to tell them apart -- which one is which mattered exactly when it
     * was hardest to see. The rows arrive newest first and stay in that order;
     * this only inserts the headings.
     *
     * Keyed on the DAY as the date is printed, not on the ISO prefix: the ISO
     * is UTC, so a scan at 00:30 local would head its own group one day off
     * from the date printed beside it.
     */
    const days = rows.reduce<Array<{ id: string; key: string; runs: AeoScoreHistoryEntry[] }>>((acc, run) => {
        const key = fmtDate(run.captured_at);
        const last = acc[acc.length - 1];
        if (last && last.key === key) last.runs.push(run);
        // A group is identified by the scan that opens it, not by its date.
        // The heading is not unique: rows out of order put the same day in two
        // groups, and two siblings keyed on "7 Oct 2026" let React reconcile
        // the wrong one and hang a day's scans under another day's heading.
        else acc.push({ id: run.assessment_id, key, runs: [run] });
        return acc;
    }, []);
    const delta = (current: number | null, previous: number | null) =>
        current !== null && previous !== null ? current - previous : null;

    return (
        <Card className="overflow-hidden">
            <CardHeader
                title="Assessment history"
                description="Every completed scan for this business, newest first. Use it to see whether the work is moving the numbers."
            />
            <CardBody className="p-0">
                {/* Above the list, not below it. At the foot of a long history
                    the total sits off-screen until you have scrolled past every
                    row — the opposite of a summary. Still says how many rows it
                    covers whenever some carry no figure: a total that silently
                    skips them reads as the whole story. */}
                {spendTotal && (
                    <p className="my-0 border-b border-gray-100 bg-gray-50 px-4 py-2 text-xs text-gray-500 tabular-nums">
                        Recorded spend: {spendTotal.text}
                    </p>
                )}
                {entries === null ? (
                    <p className="px-4 py-6 text-sm text-gray-500">Loading…</p>
                ) : rows.length === 0 ? (
                    <p className="px-4 py-6 text-sm text-gray-500">
                        No completed scans yet. The first assessment starts this history.
                    </p>
                ) : (
                    <div className="divide-y divide-gray-100">
                        {days.map(({ id, key, runs }) => (
                            <div key={id}>
                                {/* The day, once. `sticky` so it stays while
                                    its own scans scroll past, which is what
                                    makes a long history readable. */}
                                <div className="sticky top-0 z-[1] flex items-baseline gap-2 border-b border-gray-100 bg-gray-50/95 px-4 py-1.5 backdrop-blur">
                                    <span className="text-xs font-semibold text-gray-700">{key}</span>
                                    {runs.length > 1 && (
                                        <span className="text-xs text-gray-400 tabular-nums">{runs.length} scans</span>
                                    )}
                                </div>
                                <div className="divide-y divide-gray-100">
                        {runs.map((run) => {
                            const i = rows.indexOf(run);
                            // Compare against the previous SCORED run, not simply the
                            // next row, so a gated run in between does not break the
                            // chain of deltas either side of it.
                            const idxInScored = scored.indexOf(run);
                            const prev = idxInScored >= 0 ? scored[idxInScored + 1] : undefined;
                            // A scan measured on a different question set is not a
                            // step on the same ruler (#4236): its AEO has no chip, and
                            // a note says why. The break can sit on a gated run between
                            // this one and the scored run it is compared with, so every
                            // row from this one down to that one is checked. Site
                            // health does not read the questions and keeps its chip.
                            const questionBreak = prev ? questionBreakBetween(rows, i, rows.indexOf(prev)) : null;
                            const aeoDelta = prev && !questionBreak ? delta(run.aeo_score, prev.aeo_score) : null;
                            const seoDelta = prev ? delta(run.seo_score, prev.seo_score) : null;
                            const spend = summariseSpend(run.cost, run.tokens);
                            return (
                                <div key={run.assessment_id} className="flex items-center gap-4 px-4 py-3">
                                    <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-blue-50">
                                        <HistoryIcon className="h-4 w-4 text-blue-600" />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <div className="text-sm font-medium text-gray-900 tabular-nums">
                                            {/* The TIME here; the day is the
                                                heading above. Without it, two
                                                scans an hour apart were two
                                                identical rows. */}
                                            {fmtTime(run.captured_at) ?? fmtDate(run.captured_at)}
                                            {i === 0 && <span className="ml-2 text-xs font-normal tabular-nums text-gray-400">latest</span>}
                                        </div>
                                        {run.gated && (
                                            <div className="text-xs text-amber-700">
                                                This run produced no usable score
                                            </div>
                                        )}
                                        {questionBreak && (
                                            <div className="text-xs text-gray-500">
                                                New questions: AEO is not compared with the scan before it.
                                                {questionBreak.reason ? ` ${questionBreak.reason.replace(/\.+$/, "")}.` : ""}
                                            </div>
                                        )}
                                        {/* Under the date rather than in a third
                                            column: the two score cells are
                                            fixed-width and already crowd at `sm`,
                                            and spend is context for the run rather
                                            than a reading of its own. Renders
                                            nothing at all when the backend sent no
                                            figures, so an older one leaves this
                                            panel exactly as it was. */}
                                        {(spend.money || spend.tokens) && (
                                            <div className="flex items-center gap-1 text-xs text-gray-400 tabular-nums">
                                                {spend.money && (
                                                    <span>
                                                        {spend.money}
                                                        {spend.isEstimate && <span className="ml-1">est.</span>}
                                                    </span>
                                                )}
                                            {spend.money && spend.tokens && <span> · </span>}
                                            {spend.tokens && <span>{spend.tokens}</span>}
                                            {/* The breakdown behind an explicit
                                                affordance rather than the native
                                                `title` on the whole line: that
                                                one rendered as an unstyled dark
                                                box over the rows beneath it, and
                                                gave no sign it existed until you
                                                happened to hover the text. */}
                                            {spend.detail && (
                                                <Tooltip content={spend.detail}>
                                                    <InfoIcon
                                                        className="h-3 w-3 flex-shrink-0 cursor-help text-gray-300 transition-colors hover:text-gray-500"
                                                        aria-label="How this was measured"
                                                    />
                                                </Tooltip>
                                            )}
                                        </div>
                                        )}
                                    </div>
                                    {/* The two scores as their own group, with more room between
                                        them than between a label and its number — so "AEO 30 +6"
                                        and "Site Health 96" read as two readings, not one line. */}
                                    <div className="flex flex-shrink-0 items-center gap-8">
                                        <ScoreCell label="AEO" value={run.aeo_score} change={aeoDelta} className="w-32" />
                                        <ScoreCell label="Site Health" value={run.seo_score} change={seoDelta} className="w-40" />
                                    </div>
                                </div>
                            );
                        })}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </CardBody>
        </Card>
    );
}

/**
 * "14:32" from a stamp, or null when there is no readable one.
 *
 * Local time and 24-hour, as `dayTime` on the onboarding screens prints it, so
 * two parts of Oura never disagree about what time a thing happened. Null
 * rather than a guess: an unreadable stamp falls back to the date, which is
 * the one thing it is known to carry.
 */
function fmtTime(iso: string | null): string | null {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

/**
 * One score plus its movement. `null` reads as "not measured"; `0` reads as 0.
 *
 * The width is the caller's, fixed per column so every row lines up, and sized
 * for the widest reading that column can hold: "Site Health 100 +15" needs
 * about 145px, and at the old shared 112px the label broke onto two lines and
 * the chip ran into the neighbouring cell.
 */
function ScoreCell({ label, value, change, className }: {
    label: string;
    value: number | null;
    change: number | null;
    className?: string;
}) {
    return (
        // THREE SUB-COLUMNS, and the chip's is reserved whether or not there is
        // a chip (#4025 FE-20). The cell used to be `justify-end`, so a row
        // whose score had not moved had nothing on the right and its label and
        // number slid across to fill the gap -- the labels zigzagged down the
        // list and no two numbers shared a vertical line. Now the label ends
        // where every label ends, the number ends where every number ends, and
        // an absent chip leaves its space empty instead of pulling the row.
        <div className={cn("flex flex-shrink-0 items-center gap-2", className)}>
            <span className="flex-1 whitespace-nowrap text-right text-xs text-gray-400">{label}</span>
            <span className="w-8 text-right text-sm font-semibold text-gray-900 tabular-nums">
                {value === null ? "—" : value}
            </span>
            {/* The slot, always. `w-11` holds the widest chip this column can
                show ("+100"); an empty one is 11 columns of nothing, which is
                the point. */}
            <span className="flex w-11 justify-start">
                {change !== null && change !== 0 && (
                    <Chip
                        className={cn(
                            "tabular-nums",
                            change > 0
                                ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                                : "border-rose-200 bg-rose-50 text-rose-700",
                        )}
                    >
                        {change > 0 ? "+" : ""}
                        {change}
                    </Chip>
                )}
            </span>
        </div>
    );
}

/**
 * The tabs whose whole content is derived from a completed scan.
 *
 * On a business nothing has scored, every figure on these comes out of the
 * fixture fallback — a hash of the business id — so a staffer opening one reads
 * generated numbers about a real client (#3710 AC6). The others are safe: their
 * content is what somebody entered or connected, and an empty one is honestly
 * empty on its own.
 */
const SCAN_DERIVED_TABS: ReadonlySet<DetailTab> = new Set<DetailTab>([
    "dashboard", "pages", "setup", "questions",
]);

const RECIPE_NOTICE_TABS: ReadonlySet<DetailTab> = new Set<DetailTab>(["dashboard", "questions"]);

/**
 * One calm line above the screen and its link, on the kit's card surface: a
 * newer question recipe would change this set, a draft of these questions is
 * open, or a signed client's First AI check is drafted (JOURNEY-SPEC §1.1
 * row S, reviewed from onboarding, never the draft itself). Shown on arrival,
 * not in answer to a press, so it is not a live region.
 */
function NoticeLine({ icon: Icon, text, action, onAction }: { icon: LucideIcon; text: string; action: string; onAction: () => void }) {
    return (
        <div className={cn(SURFACE.card, "mb-5 flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3")}>
            <Icon aria-hidden size={15} className="shrink-0 text-blue-600" />
            <p className={cn(TYPE.tb, "m-0 min-w-0 flex-1 basis-60 text-gray-700")}>{text}</p>
            <ArrowLink onClick={onAction}>{action}</ArrowLink>
        </div>
    );
}

export function BusinessDetail({
    business,
    snapshot,
    active,
    onNavigate,
    onOpenReport,
    onRenamed,
    readOnly,
    scanPicker,
    hubFacts,
    rescanInfo,
    children,
}: {
    business: Business;
    snapshot: ScoreSnapshot;
    active: DetailTab;
    onNavigate: (v: View) => void;
    onOpenReport: () => void;
    /** Re-read the business after a rename, so every surface that prints the
     *  name agrees. Absent (fixture business) ⇒ the dialog still closes. */
    onRenamed?: () => void | Promise<void>;
    /**
     * A past scan is on screen (#3933): nothing in this chrome may write.
     *
     * Rerun and rename act on the business as it is NOW, and offering them
     * beside figures from three weeks ago invites exactly the mistake the
     * version banner exists to prevent.
     */
    readOnly?: boolean;
    /** The scan version picker, when this business has more than one. */
    scanPicker?: ReactNode;
    /**
     * What the business row says about its stage (JOURNEY-SPEC v1.1 §2.2).
     * With the New scan switch on, the header shows the stage's pills and the
     * screens inside read the stage through HubContext. Absent: today's header.
     */
    hubFacts?: HubFacts;
    /** What the Rescan sheet names: the set the last scan asked and what it cost. */
    rescanInfo?: { setVersion: number | null; costUsd: number | null };
    children?: ReactNode;
}) {
    const snackbar = useSnackbar();
    const [rescanOpen, setRescanOpen] = useState(false);
    /**
     * Renaming (ourteam-backend#5003).
     *
     * The label only — the domain under it is the key connectors, drafts,
     * knowledge and trackers are all stored against, so it is not editable from
     * here and changing one is a migration rather than a rename.
     *
     * Explicit `=== "true"`, absent ⇒ OFF: the PATCH route is not deployed
     * everywhere yet, and a pencil that opens a dialog nothing can save is
     * worse than no pencil. A 404/405 on save hides it for the rest of the
     * session too, in case a deploy disagrees with the flag.
     */
    const renameLive = import.meta.env.VITE_ENABLE_OURA_BUSINESS_RENAME === "true";
    const isStaff = useOuraRole() === "oura-staff";
    const [renameOpen, setRenameOpen] = useState(false);
    const [renameAbsent, setRenameAbsent] = useState(false);
    const canRename = isStaff && renameLive && !renameAbsent;
    // Same formatting as the Businesses table's Last Scan column, so the two
    // surfaces never state the same date differently. A business with no
    // completed scan says so rather than rendering an epoch or a blank clause.
    const lastScanDay = shortDateOrNull(snapshot?.capturedAt);
    /**
     * The inputs the last scan was created with (#3508, BE ourteam-backend#4567).
     *
     * Fetched from the assessment rather than read off the business, because
     * `inputs` rides on GET /assessments/{id} — the business payload carries
     * category and city but never the anchors and services, which are the two
     * that actually decide the ~50 questions the AEO score is computed from.
     *
     * Loaded when the modal opens rather than on mount: it is only needed for a
     * re-run, and most visits to this page never start one.
     */
    const [lastInputs, setLastInputs] = useState<AeoSubmittedInputs | null>(null);
    // buildLiveSnapshot() sets `id` to the assessment id, falling back to the
    // BUSINESS id when there is no assessment — so the two must be told apart
    // before fetching, or a business that has never been scanned would ask
    // /assessments/{businessId} and 404.
    const lastAssessmentId = snapshot.id && snapshot.id !== business.id ? snapshot.id : null;
    useEffect(() => {
        if (!rescanOpen || !lastAssessmentId) return;
        let cancelled = false;
        void getAssessment(lastAssessmentId)
            .then((a) => { if (!cancelled) setLastInputs(a?.inputs ?? null); })
            // A failure here is not fatal: the modal falls back to detect, which
            // is the behaviour this ticket replaces rather than depends on.
            .catch(() => { if (!cancelled) setLastInputs(null); });
        return () => { cancelled = true; };
    }, [rescanOpen, lastAssessmentId]);
    // Shared by both chromes, so the sidebar and the legacy tab strip can never
    // report a re-scan differently.
    const onScanStarted = (result: StartResult) => {
        setRescanOpen(false);
        if (result.ok) {
            snackbar.showSuccess(
                "Re-scan started",
                "Progress shows in the scan toast; this page updates when it finishes.",
            );
        } else if (result.kind === "duplicate") {
            snackbar.showSuccess(
                "A scan is already running",
                "This business already has a run in flight — watch the scan toast for it.",
            );
        } else if (result.kind === "validation") {
            snackbar.showError("Could not start the re-scan", result.errors.join(" · "));
        } else {
            snackbar.showError("Could not start the re-scan", result.message);
        }
    };

    /*
     * The New scan flow (switch on, staff only): "Edit the questions" opens
     * the draft page for this business, and a recipe update, when one would
     * change this set, is offered in one line above the screen. Rerun keeps
     * the old modal. The flow page hands its notice back through the router.
     */
    const navigate = useNavigate();
    const location = useLocation();
    // Not where the draft service is absent: the flow's page would only say "not available here".
    const draftFlowOn = SCAN_DRAFT_UI_ON && scanDraftMode() !== "absent" && isStaff;
    const draftHref = `${OURA_ROOT}/businesses/${encodeURIComponent(business.id)}/questions/draft`;
    const [recipeChanges, setRecipeChanges] = useState<number | null>(null);
    useEffect(() => {
        setRecipeChanges(null);
        if (!draftFlowOn) return;
        let cancelled = false;
        void recipeUpdateFor(business.id).then((res) => {
            if (!cancelled && res.ok && res.data && res.data.changed > 0) setRecipeChanges(res.data.changed);
        });
        return () => { cancelled = true; };
    }, [draftFlowOn, business.id]);
    const openFlow = useCallback(
        (to: string) => navigate(to, { state: { from: `${location.pathname}${location.search}` } }),
        [navigate, location.pathname, location.search],
    );

    /*
     * The business's stage (JOURNEY-SPEC v1.1 §2.2): the header's pills, the
     * Overview Next card and the empty states read it. New scan switch on and
     * staff only; off, the header keeps Rerun assessment and its modal.
     */
    const hubOn = draftFlowOn && !!hubFacts;
    const facts: HubFacts = hubFacts ?? {
        businessId: business.id, name: business.name, domain: business.domain,
        scanned: false, scannedAt: null, run: null, nextScanAt: null,
    };
    const hubState = useHubStage(facts, { enabled: hubOn, onboardingOn: showOnboarding, draftFlow: draftFlowOn });

    // The open draft, read once: by the stage when it is on, else here (the switch on without stage facts).
    const [ownDraft, setOwnDraft] = useState<{ id: string; kind: string; firstCheck: boolean } | null>(null);
    // Whether that read has answered: loading and failed are not "no draft", so the edit press waits
    // rather than opening a second draft beside one still being read (Jack, #4284).
    const [ownDraftRead, setOwnDraftRead] = useState<"loading" | "ready" | "failed">("loading");
    const [ownDraftTry, setOwnDraftTry] = useState(0);
    useEffect(() => {
        setOwnDraft(null);
        setOwnDraftRead("loading");
        if (!draftFlowOn || hubOn) return;
        let cancelled = false;
        void findOpenScanDraft({ businessId: business.id }).then(
            (res) => {
                if (cancelled) return;
                if (!res.ok) { setOwnDraftRead("failed"); return; }
                const d = res.data;
                if (d) setOwnDraft({ id: d.id, kind: d.kind, firstCheck: isFirstCheckDraft(d) });
                setOwnDraftRead("ready");
            },
            () => { if (!cancelled) setOwnDraftRead("failed"); },
        );
        return () => { cancelled = true; };
    }, [draftFlowOn, hubOn, business.id, ownDraftTry]);
    // A signed client's First AI check, drafted and not started: reviewed from onboarding.
    const firstCheckOpen = hubOn ? hubState.firstCheckOpen : !!ownDraft?.firstCheck;
    // An open draft of these questions (edit or recipe update), saved on Close.
    const editDraft = hubOn ? hubState.draft : ownDraft && !ownDraft.firstCheck ? ownDraft : null;
    const openDraftId = editDraft && (editDraft.kind === "edit_set" || editDraft.kind === "recipe_change") ? editDraft.id : null;
    // The flow's Close comes back to this tab rather than to the business's first screen.
    const editHref = openDraftId ? `${draftHref}?draft=${encodeURIComponent(openDraftId)}` : draftHref;

    const [rescanSheetOpen, setRescanSheetOpen] = useState(false);
    const [signedOpen, setSignedOpen] = useState(false);
    const [signing, setSigning] = useState(false);
    const refreshHub = hubState.refresh;
    const takeSignature = useCallback(async (n: number) => {
        // The Onboarding card's Use Q{n}, made here: the same write, the same words.
        setSigning(true);
        const r = await chooseSignatureQuestion(business.id, n);
        setSigning(false);
        if (!r.ok) { snackbar.showError("Could not choose the question", r.message); return; }
        snackbar.showSuccess(`Q${n} is the signature question`, "It is on the plan now.");
        refreshHub();
    }, [business.id, snackbar, refreshHub]);
    const actOnTarget = useHubActions({
        onClientSigned: () => setSignedOpen(true),
        onRescan: () => setRescanSheetOpen(true),
        onSignature: (_, n) => void takeSignature(n),
    });
    const target = useMemo(() => ({ id: business.id, name: business.name, domain: business.domain }), [business.id, business.name, business.domain]);
    const actOn = useCallback((a: HubAct) => actOnTarget(target, a), [actOnTarget, target]);
    const hub: HubContextValue | null = useMemo(
        () => (hubOn ? { ...hubState, act: actOn, busy: signing } : null),
        [hubOn, hubState, actOn, signing],
    );
    // While the stage is read, no pill: a pill that changes under the pointer is worse than none.
    // On the answers, while the first results wait for approval or the send, Edit and rescan sits
    // beside Approve results in FirstResultsBanner, so the header leaves it out there (Jovi, 20 Oct).
    const editInBanner = active === "questions" && firstResultsAwaitSend(hubState.onboarding);
    const hubPills: HubPill[] | undefined = hubOn ? (editInBanner ? hubState.pills.filter((p) => !isEditRescanPill(p)) : hubState.pills) : undefined;
    // One blue press per screen, and it is the page's own: Share report stays white on every tab
    // (Dana and Angel, 15 Oct: a blue Share report competed with the Next card; Jack, 22 Oct: and
    // with Delivery's and Onboarding's own blue press once the stage was off).
    const shareSecondary = true;
    const hubSheets = hubOn ? (
        <>
            <RescanSheet
                open={rescanSheetOpen}
                businessId={business.id}
                businessName={business.name}
                domain={business.domain}
                setVersion={rescanInfo?.setVersion ?? null}
                costUsd={rescanInfo?.costUsd ?? null}
                nextScanAt={facts.nextScanAt}
                onClose={() => setRescanSheetOpen(false)}
                onStarted={() => {
                    setRescanSheetOpen(false);
                    snackbar.showSuccess("Rescan started", `About ${FIRST_CHECK_MINUTES} minutes.`);
                }}
            />
            <NewClientDialog
                open={signedOpen}
                onClose={() => setSignedOpen(false)}
                existing={{ businessId: business.id, name: business.name, domain: business.domain }}
                onCreated={() => {
                    // The plan exists now: the stage, and Delivery's "no contract" line, read again without a reload.
                    hubState.refresh();
                    announceDeliveryChange();
                }}
                onOpen={() => { setSignedOpen(false); onNavigate("onboarding"); }}
                returnFocus={() => document.querySelector<HTMLElement>("[data-next-card] button")}
            />
        </>
    ) : null;
    useDraftNoticeSnackbar();
    // A scan running for this business (New scan switch on): the tag that stays after the snackbar goes.
    // Not where the Next card already shows it running (its violet tag and bar): one voice, and no jump.
    const scanningLabel = useScanningSites().labelFor(business.domain, business.id);
    /*
     * The header's scan fact. "Never scanned" only when nothing ran and nothing is
     * running: a scan in progress (the pitch, or the first AI check) says Scanning,
     * and a first AI check the onboarding record holds gives its day even before
     * the business row has caught up with it.
     */
    const firstCheck = hubState.stage?.kind === "onboarding" ? hubState.stage.onboarding.scan : null;
    /*
     * One Setup (Jack, 20 Oct 2026): nothing reaches the client until the results and plan
     * are sent, so Share report stays shut until then, with the reason on it.
     */
    const setupRecord = hubState.stage?.kind === "onboarding" ? hubState.stage.onboarding : null;
    const shareLocked = setupRecord && setupRecord.resultsApprovedAt !== undefined && setupRecord.resultsSentAt === null
        ? `Opens after you send the results to ${callName(setupRecord.contact.name) || "the client"}.`
        : null;
    const knownScanDay = lastScanDay
        ?? shortDateOrNull(facts.scannedAt)
        ?? (firstCheck?.status === "complete" ? shortDateOrNull(firstCheck.finishedAt) : null);
    const runningNow = !!scanningLabel || facts.run?.status === "running" || firstCheck?.status === "running";
    const lastScanLabel = runningNow && !knownScanDay ? "AI check running" : knownScanDay ? `Last AI check ${knownScanDay}` : "No AI check yet";
    const nextShowsRun = hubOn && !!hubState.next
        && (active === "dashboard" || (active === "onboarding" && hubState.stage?.kind === "onboarding"));
    const scanningTag = scanningLabel && !nextShowsRun ? <Tag tone="violet">{scanningLabel}</Tag> : null;
    // On the screens about the questions only: Overview and Visibility.
    // An open draft is said first: Review the change would only reopen it.
    const showNotice = draftFlowOn && !readOnly && RECIPE_NOTICE_TABS.has(active);
    // A first_check draft is said first and alone: no recipe update is offered on it (H7-a).
    // On Overview the stage's Next card already names the drafted first check (one voice, §2.2).
    // Once the first check has run, its results card (Approve, then Approved) speaks for it: the "drafted" line is stale.
    const recipeNotice = !showNotice ? null : firstCheckOpen ? ((hubOn && active === "dashboard") || firstCheck?.status === "complete" ? null : (
        <NoticeLine
            icon={FilePenLineIcon}
            text="The first AI check is drafted."
            action="Open onboarding"
            onAction={() => navigate(`${OURA_ROOT}/businesses/${encodeURIComponent(business.id)}/onboarding`)}
        />
    )) : openDraftId ? (
        <NoticeLine icon={FilePenLineIcon} text="A draft of these questions is open." action="Continue the draft" onAction={() => openFlow(editHref)} />
    ) : recipeChanges !== null ? (
        <NoticeLine
            icon={SparklesIcon}
            text={`A new question recipe would change ${recipeChanges} of these 50.`}
            action="Review the change"
            onAction={() => openFlow(`${draftHref}?kind=recipe_change`)}
        />
    ) : null;

    const measuring = isMeasureView(active as View);
    if (isBusinessSidebarLive) {
        return (
            <SidebarChrome
                business={business}
                active={active}
                lastScanLabel={lastScanLabel}
                onNavigate={onNavigate}
                onOpenReport={onOpenReport}
                onRescan={() => setRescanOpen(true)}
                onRename={() => setRenameOpen(true)}
                canRename={canRename && !readOnly}
                readOnly={readOnly}
                scanPicker={scanPicker}
                hubPills={hubPills}
                onHubAct={actOn}
                scanningTag={scanningTag}
                shareSecondary={shareSecondary}
                shareLocked={shareLocked}
            >
                {recipeNotice}
                <HubContext.Provider value={hub}>
                    {active === "history" ? <ScoreHistoryPanel businessId={business.id} /> : children}
                </HubContext.Provider>
                {hubSheets}
                {!hubOn && <NewScanModal
                    open={rescanOpen}
                    businessId={business.id}
                    prefillDomain={business.domain}
                    prefillInputs={lastInputs}
                    prefillName={lastInputs ? business.name : undefined}
                    prefillCategory={lastInputs ? business.vertical : undefined}
                    onClose={() => setRescanOpen(false)}
                    onStarted={onScanStarted}
                />}
                {renameOpen && (
                    <RenameDialog
                        business={business}
                        onClose={() => setRenameOpen(false)}
                        onSaved={() => onRenamed?.()}
                        onRouteAbsent={() => setRenameAbsent(true)}
                    />
                )}
            </SidebarChrome>
        );
    }

    return (
        <div>
            {/* Breadcrumb — detail pages navigate up, not "back" (house §12) */}
            <nav className="mb-3 flex items-center text-base font-medium">
                <Link to={`${OURA_ROOT}/businesses`} className="text-gray-500 transition-colors hover:text-gray-700">
                    Businesses
                </Link>
                <ChevronRightIcon className="mx-2 h-4 w-4 text-gray-400" />
                <span className="text-gray-900">{business.name}</span>
            </nav>

            {/* Business header */}
            <div className="mb-4 flex items-start justify-between gap-4">
                <div className="flex items-center gap-3">
                    {/* A building glyph, not derived initials. Business names
                        here are usually multi-word, so the two-letter initials
                        were meaningless as often as not; the icon reads as
                        "a business" immediately and matches the identity-icon
                        pattern used in every other table first column. */}
                    <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg bg-blue-50">
                        <Building2Icon className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-semibold text-gray-900">{business.name}</h1>
                        {/* #3510: the header never said when this was last
                            scanned, so every number below it was undated. Added as
                            a clause here rather than by reusing ScanFreshness —
                            that component also ships a "Refresh now" button whose
                            handler is a prototype stub, and a fake button on a
                            staff surface is worse than a missing one. */}
                        <p className="mt-0.5 text-sm text-gray-500">
                            {joinDotted(business.domain, business.vertical, business.city, lastScanLabel)}
                        </p>
                        {scanningTag && <p className="m-0 mt-1.5">{scanningTag}</p>}
                    </div>
                </div>
                <div className="mt-1 flex flex-shrink-0 items-center gap-2">
                    {/* Opens the scan flow prefilled with this domain rather than
                        firing a silent run. The anchors and services are what
                        generate the ~50 questions and the detector gets them
                        wrong often enough to matter, so they get confirmed
                        before we spend a paid run on them. */}
                    {hubPills ? (
                        !readOnly && hubPills.filter((p) => measuring || p.act.kind !== "rescan").map((p) => {
                            const Icon = HUB_PILL_ICON[p.act.kind];
                            return (
                                <Button key={p.label} variant={p.primary ? undefined : "outline"} onClick={() => actOn(p.act)}>
                                    <Icon className="h-4 w-4" />
                                    {p.label}
                                </Button>
                            );
                        })
                    ) : (
                        <>
                            {draftFlowOn && (ownDraftRead === "failed" ? (
                                <Button variant="outline" onClick={() => setOwnDraftTry((n) => n + 1)}>
                                    <ListChecksIcon className="h-4 w-4" />
                                    Couldn't check for a draft. Try again
                                </Button>
                            ) : (
                                <Button variant="outline" disabled={readOnly || ownDraftRead === "loading"} onClick={() => openFlow(editHref)}>
                                    <ListChecksIcon className="h-4 w-4" />
                                    {openDraftId ? "Continue the draft" : "Edit the questions"}
                                </Button>
                            ))}
                            {measuring && (
                                <Button variant="outline" onClick={() => setRescanOpen(true)}>
                                    <RefreshCwIcon className="h-4 w-4" />
                                    Run AI check again
                                </Button>
                            )}
                        </>
                    )}
                    {!measuring ? null : shareLocked ? (
                        <span title={shareLocked} className="inline-flex">
                            <Button variant="outline" disabled aria-label={`Share report. ${shareLocked}`}>
                                <ShareIcon className="h-4 w-4" />
                                Share report
                            </Button>
                        </span>
                    ) : (
                        <Button variant={shareSecondary ? "outline" : undefined} onClick={onOpenReport}>
                            <ShareIcon className="h-4 w-4" />
                            Share report
                        </Button>
                    )}
                </div>
            </div>

            {/* Page tabs (house §13A: icon + label, blue active).

                `overflow-x-auto` + `flex-shrink-0` on the buttons is what keeps
                this strip from widening the PAGE. Eight tabs at icon + label
                measure ~1040px and could not shrink, so on any viewport whose
                content column is narrower than that — 1280px screen minus the
                256px sidebar leaves 1024 — the strip pushed the document into a
                horizontal scroll. The Businesses list has no tab strip, so the
                scrollbar appeared the moment a business was opened and the
                fixed sidebar then sat over the content: the reported "sidebar
                minimises when you open a company".

                Scrolling the strip inside itself keeps the overflow where it
                belongs. The tabs do not wrap: a second row of tabs moves every
                control below it and reads as two groups.

                `overflow-y-hidden` is load-bearing, not tidying. CSS computes
                `overflow-y: visible` to `auto` the moment the other axis is not
                visible, so `overflow-x-auto` alone silently made this box
                vertically scrollable — and the buttons' `-mb-px`, which is what
                sits the active underline on the border, pushes the content one
                pixel past it. That one pixel was enough: the strip grew a
                VERTICAL scrollbar down its right edge on a screen where every
                tab already fitted, which is what got reported.

                `scrollbar-hide` covers the horizontal one. It still scrolls on a
                narrow window, because the alternative is tabs nobody can reach —
                it just does it without a bar across the strip. */}
            <div className="mb-4 flex gap-1 overflow-x-auto overflow-y-hidden scrollbar-hide border-b border-gray-200">
                {SHOWN_TABS.filter((t) => t.view !== "reviews" || showClientApprovals).map((t) => {
                    // Knowledge is the second tab inside Documents.
                    const isActive = active === t.view || (t.view === "documents" && active === "knowledge");
                    const Icon = t.icon;
                    return (
                        <button
                            key={t.view}
                            onClick={() => onNavigate(t.view)}
                            className={cn(
                                "-mb-px flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap border-b-2 px-4 py-2.5 text-sm font-medium transition-colors",
                                isActive
                                    ? "border-blue-600 text-blue-700"
                                    : "border-transparent text-gray-500 hover:text-gray-700",
                            )}
                        >
                            <Icon className="h-3.5 w-3.5" />
                            {t.label}
                        </button>
                    );
                })}
            </div>

            {/* Nothing has scored this business, and this tab is built entirely
                out of scan results — so the fixture fallback would be filling it
                with numbers about a business nobody has measured. Name what is
                missing AND when it arrives: "not yet" on its own reads as a
                fault rather than a step that has not happened. */}
            {recipeNotice}

            <HubContext.Provider value={hub}>
            {active === "history" ? (
                <ScoreHistoryPanel businessId={business.id} />
            ) : !lastAssessmentId && SCAN_DERIVED_TABS.has(active) ? (
                <Card>
                    <CardBody>
                        <EmptyState
                            icon={SearchCheckIcon}
                            title="This business has not been scanned yet"
                            description={hubOn
                                ? "Overview, Pages, Site Audit and AI Visibility are all built from a completed scan."
                                : "Overview, Pages, Site Audit and AI Visibility are all built from a completed scan. Run one with Run AI check again above and they fill in when it finishes."}
                        />
                    </CardBody>
                </Card>
            ) : (
                children
            )}
            </HubContext.Provider>
            {hubSheets}

            {!hubOn && <NewScanModal
                open={rescanOpen}
                businessId={business.id}
                prefillDomain={business.domain}
                // #3508: re-run from what the last scan submitted, not from a
                // fresh detect crawl that returns page headings as "services".
                prefillInputs={lastInputs}
                prefillName={lastInputs ? business.name : undefined}
                prefillCategory={lastInputs ? business.vertical : undefined}
                onClose={() => setRescanOpen(false)}
                onStarted={onScanStarted}
            />}
        </div>
    );
}
